import { Switch, Route, Redirect } from "wouter";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/sonner";
import { useSession } from "@/lib/auth-client";
import { Auth } from "@/pages/auth";
import { List } from "@/pages/list";
import { Capture } from "@/pages/capture";
import { Review } from "@/pages/review";
import { Detail } from "@/pages/detail";
import { Report } from "@/pages/report";
import { OrgBootstrap } from "@/pages/org-bootstrap";
import { BillingBanner } from "@/components/billing";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1 },
  },
});

function Routes() {
  const { data: session, isPending } = useSession();

  if (isPending) {
    return (
      <div className="min-h-dvh flex items-center justify-center text-sm text-muted-foreground">
        Loading…
      </div>
    );
  }

  if (!session) {
    return (
        <Switch>
            <Route path="/auth" component={Auth} />
            <Route><Redirect to="/auth" /></Route>
        </Switch>
    );
  }

  // Signed in but no active org yet → silent bootstrap, mirroring
  // interchange-web. The driver never sees the word "organization".
  if (!session.session.activeOrganizationId) {
    return <OrgBootstrap />;
  }

  // The billing strip sits above every signed-in screen — a trial countdown,
  // or the paywall once it lapses. It renders nothing for an active
  // subscriber (beyond a Manage billing link), and nothing at all while the
  // status is still loading, so it never flashes across a page load.
  //
  // Above the Switch rather than inside each page BECAUSE viewing stays
  // free: a blocked driver still gets their whole app underneath it.
  return (
    <>
      <BillingBanner />
      <Switch>
        {/* Children form, like Report: List takes the session's user id so
            the first-run tour can key its "already seen" flag per driver. */}
        <Route path="/"><List userId={session.user.id} /></Route>
        <Route path="/capture" component={Capture} />
        {/* Children form, not `component=`: Report takes an optional
            `today` (injectable business day), which the route's own props
            don't satisfy. */}
        <Route path="/report"><Report /></Route>
        {/* Review is the post-capture pricing step; detail is the read
            view of an existing load. Both address one load by id, so the
            more specific route is declared first. */}
        <Route path="/l/:id/review" component={Review} />
        <Route path="/l/:id" component={Detail} />
        <Route><Redirect to="/" /></Route>
      </Switch>
    </>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Routes />
      <Toaster />
    </QueryClientProvider>
  );
}
